import { Fragment, useState } from 'react';
import { api, type PoEmailRow, type UnackRow } from '../api';
import { label, useLoad } from '../lib';

// Did the vendor get the PO? The released POs no vendor has acknowledged (commerce's port of Intellam's
// `unack_pos_email` report — 1 hour to 2 days old, both channels), the PO email send log, who the next
// send goes to, and a manual resend. The log shows address COUNTS only: commerce masks addresses at its
// boundary, and the Recipients view is where staff see the vendor's contact record. A resend mails a
// live vendor (admin/ops), and commerce refuses one for any PO that is not released (pending).
const LOG_STATUSES = ['', 'sent', 'skipped', 'no_recipient', 'error'];

// The send log's statuses on the EDI queue's status chips: sent = delivered, skipped = held back on
// purpose (the send gate is off, a redirected test send), anything else failed.
const tone = (s: string | null) => (s === 'sent' ? 'pushed' : s === 'skipped' ? 'manual' : 'failed');

// A pg timestamp ('2026-08-10 08:05:21-04') as 'Aug 10 08:05', the year only when it is not this one.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function When({ at }: { at: string | null }) {
  if (!at) return <>—</>;
  const [y, m, d] = at.slice(0, 10).split('-');
  const year = Number(y) === new Date().getFullYear() ? '' : ` ${y}`;
  return <>{MONTHS[Number(m) - 1]} {Number(d)}{year} {at.slice(11, 16)}</>;
}

const waited = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  return h ? `${h} h ${minutes % 60} m` : `${minutes} m`;
};

export function PoDeliveryPanel({ canOps }: { canOps: boolean }) {
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const unack = useLoad(() => api.commerceUnacknowledged());
  const log = useLoad(() => api.commercePoEmails(status), [status]);

  async function resend(orderId: number, po: string, vendor: string | null) {
    if (!window.confirm(`Email PO ${po} to ${vendor ?? 'the vendor'} again? A link vendor gets a fresh acknowledge link; the old one keeps working.`)) return;
    setBusy(orderId);
    setMsg(null);
    try {
      const r = await api.commerceResendPo(orderId);
      setMsg(r.status === 'sent' ? `✓ PO ${po} sent to ${vendor ?? 'the vendor'}`
        : r.status === 'no_recipient' ? `⚠ PO ${po}: ${vendor ?? 'the vendor'} has no ordering email on file — add one on the Vendors screen`
        : r.status === 'skipped' ? `⚠ PO ${po} was not sent: ${r.reason ?? r.row?.error ?? 'skipped'}`
        : `⚠ PO ${po} failed: ${r.reason ?? r.row?.error ?? 'error'}`);
      unack.reload();
      log.reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  const toggle = (key: string) => setOpen(open === key ? null : key);
  const rowActions = (key: string, orderId: number, po: string, vendor: string | null) => (
    <td className="actions">
      <button className="btn-sm ghost" onClick={() => toggle(key)}>{open === key ? 'Hide' : 'Recipients'}</button>
      {canOps && <button className="btn-sm" disabled={busy === orderId} onClick={() => resend(orderId, po, vendor)}>{busy === orderId ? 'Sending…' : 'Resend'}</button>}
    </td>
  );

  return (
    <div className="stack">
      <h1>PO Delivery</h1>
      <p className="sub">Released POs no vendor has acknowledged, every PO email sent, who the next one goes to, and a resend</p>
      {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`}>{msg}</p>}

      <section className="card">
        <h2>Unacknowledged — released more than an hour ago, less than two days</h2>
        {unack.err && <p className="msg bad">⚠ {unack.err}</p>}
        <table>
          <thead><tr><th>PO #</th><th>Vendor</th><th>Channel</th><th>Placed</th><th className="num">Waiting</th><th>Delivery</th><th>Last email</th><th></th></tr></thead>
          <tbody>
            {(unack.data ?? []).map((r: UnackRow) => {
              const po = r.po_number ?? r.increment_id;
              const key = `u${r.id}`;
              return (
                <Fragment key={key}>
                  <tr>
                    <td className="mono">{po}</td>
                    <td>{r.vendor ?? '—'}</td>
                    <td><span className={`pill${r.channel === 'edi' ? ' edi' : ' type'}`}>{r.channel === 'edi' ? 'EDI' : 'Link'}</span></td>
                    <td className="mono muted"><When at={r.created_at} /></td>
                    <td className="num">{waited(r.minutes_in_queue)}</td>
                    <td className="mono muted">{r.delivery_date ?? '—'}</td>
                    <td>{r.email_status ? <span className={`edi-status ${tone(r.email_status)}`}>{label(r.email_status)}</span> : <span className="muted">none</span>}</td>
                    {rowActions(key, r.id, po, r.vendor)}
                  </tr>
                  {open === key && <tr><td colSpan={8}><Recipients orderId={r.id} /></td></tr>}
                </Fragment>
              );
            })}
            {unack.data && unack.data.length === 0 && <tr><td colSpan={8} className="loading">Every released PO from the last two days is acknowledged.</td></tr>}
          </tbody>
        </table>
        {!unack.data && !unack.err && <p className="loading">Loading…</p>}
      </section>

      <section className="card">
        <h2>PO email log</h2>
        <div className="stack">
          <div className="toolbar">
            {LOG_STATUSES.map((s) => (
              <button key={s || 'all'} className={'chip' + (status === s ? ' on' : '')} onClick={() => setStatus(s)}>{s ? label(s) : 'All'}</button>
            ))}
          </div>
          {log.err && <p className="msg bad">⚠ {log.err}</p>}
          <table>
            <thead><tr><th>PO # · sent</th><th>Vendor</th><th>Channel</th><th>Status</th><th>Subject / reason</th><th></th></tr></thead>
            <tbody>
              {(log.data ?? []).map((r: PoEmailRow) => {
                const po = r.increment_id ?? `order ${r.order_id}`;
                const key = `l${r.id}`;
                return (
                  <Fragment key={key}>
                    <tr>
                      <td className="mono">{po}<div className="muted"><When at={r.sent_at ?? r.created_at} /></div></td>
                      <td>{r.vendor ?? '—'}</td>
                      <td>
                        <span className={`pill${r.channel === 'edi' ? ' edi' : ' type'}`}>{r.channel === 'edi' ? 'EDI' : 'Link'}</span>
                        {!!r.to_count && <div className="muted small">{r.to_count} to{r.cc_count ? ` · ${r.cc_count} cc` : ''}</div>}
                      </td>
                      <td><span className={`edi-status ${tone(r.status)}`}>{label(r.status)}</span>{r.redirected && <span className="pill info">redirected</span>}</td>
                      <td className="small">{r.error ? <span className="muted">{r.error}</span> : r.subject ?? '—'}</td>
                      {rowActions(key, r.order_id, po, r.vendor)}
                    </tr>
                    {open === key && <tr><td colSpan={6}><Recipients orderId={r.order_id} /></td></tr>}
                  </Fragment>
                );
              })}
              {log.data && log.data.length === 0 && <tr><td colSpan={6} className="loading">No {status ? `${label(status)} ` : ''}PO emails.</td></tr>}
            </tbody>
          </table>
          {log.data && log.data.length === 200 && <p className="muted small">Showing the newest 200.</p>}
          {!log.data && !log.err && <p className="loading">Loading…</p>}
        </div>
      </section>
    </div>
  );
}

// Who this order's PO email goes to right now — the send path's own resolution, without sending.
function Recipients({ orderId }: { orderId: number }) {
  const { data, err } = useLoad(() => api.commercePoRecipients(orderId), [orderId]);
  if (err) return <p className="msg bad">⚠ {err}</p>;
  if (!data) return <p className="loading">Loading recipients…</p>;
  const list = (xs: string[]) => (xs.length ? xs.join(', ') : '—');
  return (
    <p className="small">
      {data.to.length === 0 ? <span className="muted">{data.vendor ?? 'This vendor'} has no ordering email on file — a send would go nowhere.</span>
        : <>The next {data.channel === 'edi' ? 'EDI' : 'link'} PO email goes to <strong>{list(data.to)}</strong></>}
      {data.cc.length > 0 && <> · cc {list(data.cc)}</>}{data.bcc.length > 0 && <> · bcc {list(data.bcc)}</>}
      {data.accountNumber && <span className="muted"> · account # {data.accountNumber}</span>}
    </p>
  );
}
